import React from 'react';
import { Routes, Route } from 'react-router-dom';
import AdminDashboard from "./lists/AdminDashboard";
import { DataParams, ListType } from "../../types/admin";
import { ADMIN_LANG, DEFAULT_LANG } from "../../settings";
import AdminList from "./lists/AdminList";
import { useAppDispatch } from "../../app/store";
import { openModal as openThemeModal } from "../../app/slices/modals/themeModal";
import { openModal as openGameModal } from "../../app/slices/modals/gameEditModal";
import { openModal as openUserModal } from "../../app/slices/modals/userModal";
import { openModal as openTeamModal } from "../../app/slices/modals/teamModal";
import { openModal as openFormModal } from "../../app/slices/modals/formModal";
import { openModal as openLinkModal } from "../../app/slices/modals/linkModal";
import { openModal as openCollectionModal } from "../../app/slices/modals/collectionEditModal";
import SessionView from "./SessionView";
import QuestionView from "./QuestionView";
import GameView from "./GameView";
import UserView from "./UserView";
import ErrorPage from "./ErrorPage";


const userHeaders = [
    { key: 'name', label: ADMIN_LANG.name, sortable: true },
    { key: 'company', label: ADMIN_LANG.company, sortable: true },
    { key: 'username', label: ADMIN_LANG.username, sortable: true },
    { key: 'email', label: ADMIN_LANG.email, sortable: true },
    { key: 'is_active', label: ADMIN_LANG.is_active, sortable: true },
    { key: 'is_staff', label: ADMIN_LANG.is_staff, sortable: true },
    { key: 'is_superuser', label: ADMIN_LANG.is_superuser, sortable: true },
    { key: 'date_joined', label: ADMIN_LANG.date_joined, sortable: true },
    { key: 'last_seen', label: ADMIN_LANG.last_seen, sortable: true },
];

const teamHeaders = [
    { key: 'name', label: ADMIN_LANG.name, sortable: false },
    { key: 'users', label: ADMIN_LANG.users, sortable: false },
    { key: 'address', label: ADMIN_LANG.invitation_link, sortable: false },
    { key: 'allowed_domains', label: ADMIN_LANG.allowed_domains, sortable: false },
    { key: 'created', label: ADMIN_LANG.created, sortable: false },
];

const themeHeaders = [
    { key: 'image', label: ADMIN_LANG.image, sortable: false },
    { key: 'name', label: ADMIN_LANG.name, sortable: true },
    { key: 'used_in', label: ADMIN_LANG.used_in_games, sortable: false },
    { key: 'created', label: ADMIN_LANG.created, sortable: true },
    { key: 'modified', label: ADMIN_LANG.modified, sortable: true },
];

const questionHeaders = [
    { key: 'title', label: ADMIN_LANG.question, sortable: true },
    { key: 'type', label: ADMIN_LANG.type, sortable: false },
    { key: 'languages', label: ADMIN_LANG.languages, sortable: false },
    { key: 'played', label: ADMIN_LANG.played, sortable: true },
    { key: 'game', label: ADMIN_LANG.game, sortable: true },
    { key: 'created', label: ADMIN_LANG.created, sortable: true },
    { key: 'modified', label: ADMIN_LANG.modified, sortable: true },
];

const gameHeaders = [
    { key: 'name', label: ADMIN_LANG.name, sortable: true },
    { key: 'ordering', label: ADMIN_LANG.list_order, sortable: true },
    { key: 'languages', label: ADMIN_LANG.languages, sortable: false },
    { key: 'pages', label: ADMIN_LANG.pages, sortable: true },
    { key: 'played', label: ADMIN_LANG.played, sortable: true },
    { key: 'modified', label: ADMIN_LANG.modified, sortable: true },
    { key: 'created', label: ADMIN_LANG.created, sortable: true }
];

const formHeaders = [
    { key: 'name', label: ADMIN_LANG.name, sortable: false },
    { key: 'used_in', label: ADMIN_LANG.used_in, sortable: false },
    { key: 'languages', label: ADMIN_LANG.languages, sortable: false },
    { key: 'fields', label: ADMIN_LANG.fields, sortable: false },
    { key: 'created', label: ADMIN_LANG.created, sortable: false },
    { key: 'export', label: '', sortable: false },
];

const linkHeaders = [
    { key: 'name', label: ADMIN_LANG.name, sortable: false },
    { key: 'game', label: ADMIN_LANG.game, sortable: false },
    { key: 'address', label: ADMIN_LANG.invitation_link, sortable: false },
    // {key: 'team', label: ADMIN_LANG.team, sortable: true},
    { key: 'usages', label: ADMIN_LANG.use_amount, sortable: false },
    { key: 'expires', label: ADMIN_LANG.use_end, sortable: false },
    { key: 'created_by', label: ADMIN_LANG.created_by, sortable: false },
    { key: 'created', label: ADMIN_LANG.created, sortable: false },
];

const collectionHeaders = [
    { key: 'name', label: ADMIN_LANG.name, sortable: true },
    { key: 'games_count', label: ADMIN_LANG.games, sortable: false },
    { key: 'modified', label: ADMIN_LANG.modified, sortable: true },
    { key: 'created', label: ADMIN_LANG.created, sortable: true },
];

type Props = { params: DataParams; }

const AdminRoutes = ({ params }: Props) => {
    const dispatch = useAppDispatch();

    const handleItemAdd = (type: ListType) => {
        if (type === 'users') {
            dispatch(openUserModal({ user_id: null }))
        } else if (type === 'teams') {
            dispatch(openTeamModal({ team_id: null }));
        } else if (type === 'themes') {
            dispatch(openThemeModal({ theme_id: null }));
        } else if (type === 'games') {
            dispatch(openGameModal({ game_id: null, version_id: null, language: ADMIN_LANG.default_language }));
        } else if (type === 'forms') {
            dispatch(openFormModal({ form_id: null }));
        } else if (type === 'links') {
            dispatch(openLinkModal({ link_id: null }));
        } else if (type === 'collections') {
            dispatch(openCollectionModal({ collection_id: null, language: DEFAULT_LANG }));
        }
    }

    return (
        <Routes>
            <Route path="/" element={<AdminDashboard params={params} />} />
            <Route path="games/" element={<AdminList params={params}
                headers={gameHeaders}
                listType='games'
                addItem={handleItemAdd}
                filters={['language', 'status']} />} />
            <Route path="games/:game_id/versions/:version_id" element={<GameView params={params} />} />
            <Route path="questions/" element={<AdminList params={params}
                headers={questionHeaders}
                listType='questions'
                addItem={handleItemAdd}
                filters={['language', 'type', 'game']} />} />
            <Route path="questions/:question_id/" element={<QuestionView params={params} />} />
            <Route path="questions/:question_id/versions/:version_id" element={<QuestionView params={params} />} />
            <Route path="themes/" element={<AdminList params={params}
                headers={themeHeaders}
                listType='themes'
                addItem={handleItemAdd}
                filters={[]} />} />
            <Route path="users/" element={<AdminList params={params}
                headers={userHeaders}
                listType='users'
                addItem={handleItemAdd}
                filters={['team', 'company', 'is_active', 'is_staff', 'is_superuser']} />} />
            <Route path="users/:user_id" element={<UserView />} />
            <Route path="teams/" element={<AdminList params={params}
                headers={teamHeaders}
                listType='teams'
                addItem={handleItemAdd}
                filters={[]} />} />
            <Route path="forms/" element={<AdminList params={params}
                headers={formHeaders}
                listType='forms'
                addItem={handleItemAdd}
                filters={[]} />}
            />
            <Route path="links/" element={<AdminList params={params}
                headers={linkHeaders}
                listType='links'
                filters={['team', 'game']}
                addItem={handleItemAdd} />} />
            <Route path="collections/" element={<AdminList params={params}
                headers={collectionHeaders}
                listType='collections'
                addItem={handleItemAdd}
                filters={[]} />} />
            <Route path="sessions/:session_id" element={<SessionView />} />
            <Route path="*" element={<ErrorPage />} />
        </Routes>
    );
};

export default AdminRoutes;
